import { createContext, useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import Cookies from "js-cookie";

import { APIAdminMe } from "./api_admin_auth";
import { APIAdminLogout } from "./api_admin_auth";

export const AdminContext = createContext(null);
export const useAdmin = () => useContext(AdminContext);

export const ADMIN_TOKEN_COOKIE = "adminToken";

export default function AdminProvider({ children }) {
  const navigate = useNavigate();
  const location = useLocation();

  const [adminToken, setAdminToken] = useState(null);
  const [admin, setAdmin] = useState(null);
  // 'idle' | 'verifying' | 'authed' | 'unauthed'
  const [authStatus, setAuthStatus] = useState("idle");

  const loadAdmin = async (token) => {
    try {
      const res = await APIAdminMe(token);
      setAdmin(res?.admin || null);
      return true;
    } catch (e) {
      if (e?.response?.status === 401 || e?.response?.status === 403) return false;
      return true; // сеть/500 — токен оставляем
    }
  };

  const applyLoginSuccess = async ({ token, admin: adminDto }) => {
    Cookies.set(ADMIN_TOKEN_COOKIE, token, { expires: 1 }); // короче срок, чем у партнёра
    setAdminToken(token);
    if (adminDto) setAdmin(adminDto);
    else await loadAdmin(token);
    setAuthStatus("authed");
  };

  const logout = async () => {
    try {
      if (adminToken) await APIAdminLogout(adminToken);
    } catch (e) { /* не важно */ }
    Cookies.remove(ADMIN_TOKEN_COOKIE);
    setAdminToken(null);
    setAdmin(null);
    setAuthStatus("unauthed");
    navigate("/admin/login");
  };

  useEffect(() => {
    const verify = async () => {
      const token = Cookies.get(ADMIN_TOKEN_COOKIE);
      if (!token) { setAuthStatus("unauthed"); return; }
      setAuthStatus("verifying");
      const ok = await loadAdmin(token);
      if (ok) { setAdminToken(token); setAuthStatus("authed"); }
      else { Cookies.remove(ADMIN_TOKEN_COOKIE); setAuthStatus("unauthed"); }
    };
    verify();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const isAdminArea = location.pathname.startsWith("/admin");
    const isPublic = location.pathname === "/admin/login";
    if (isAdminArea && !isPublic && authStatus === "unauthed") {
      navigate("/admin/login", { replace: true });
    }
  }, [location.pathname, authStatus, navigate]);

  const canEdit = !!admin && (admin.is_superadmin || ["moderator", "superadmin"].includes(admin.role));
  const isSuperadmin = !!admin && (admin.is_superadmin || admin.role === "superadmin");

  // Права приходят кодами из /auth/me/. Суперадмину разрешено всё
  // независимо от списка — на сервере то же правило.
  const permissions = new Set(admin?.permissions || []);
  const has = (code) => isSuperadmin || permissions.has(code);

  const value = {
    adminToken,
    admin,
    authStatus,
    canEdit,
    isSuperadmin,
    permissions,
    has,
    applyLoginSuccess,
    logout,
    reloadAdmin: () => adminToken && loadAdmin(adminToken),
  };

  return <AdminContext.Provider value={value}>{children}</AdminContext.Provider>;
}